import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listDeliveryBoys,
  createDeliveryBoy,
  updateDeliveryBoy,
  deleteDeliveryBoy,
} from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, Phone, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useBranch } from "@/context/BranchContext";
import MinGuaranteesTab from "@/components/MinGuaranteesTab";

const emptyForm = { name: "", phone: "", min_guarantee: 0, active: true };

export default function DeliveryBoys() {
  const [boys, setBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { branchId } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      setBoys(await listDeliveryBoys());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setForm({
      name: b.name,
      phone: b.phone || "",
      min_guarantee: b.min_guarantee || 0,
      active: b.active !== false,
    });
    setEditingId(b.id);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      min_guarantee: parseFloat(form.min_guarantee) || 0,
      active: !!form.active,
    };
    try {
      if (editingId) {
        await updateDeliveryBoy(editingId, payload);
        toast.success("Delivery boy updated");
      } else {
        await createDeliveryBoy(payload);
        toast.success("Delivery boy added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDeliveryBoy(deleteId);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="delivery-boys-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">Manage</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
          Team
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Delivery boys and their minimum earning guarantee presets.
        </p>
      </div>

      <Tabs defaultValue="boys" className="space-y-4" data-testid="team-tabs">
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="boys" data-testid="tab-boys">Delivery Boys</TabsTrigger>
          <TabsTrigger value="min-guarantees" data-testid="tab-min-guarantees">
            Minimum Guarantees
          </TabsTrigger>
        </TabsList>

        <TabsContent value="boys" className="space-y-4">
          <div className="flex items-end justify-between">
            <p className="text-xs text-zinc-500">Add your team members and set their default minimum earning guarantee.</p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
              data-testid="add-delivery-boy-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Delivery Boy
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingId ? "Edit Delivery Boy" : "Add Delivery Boy"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Ramesh Kumar"
                    data-testid="db-name-input"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="e.g. 98765 43210"
                    data-testid="db-phone-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="min-g">Minimum Guarantee (₹/day)</Label>
                  <Input
                    id="min-g"
                    type="number"
                    step="0.01"
                    className="currency-input"
                    value={form.min_guarantee}
                    onChange={(e) => setForm({ ...form, min_guarantee: e.target.value })}
                    placeholder="0.00"
                    data-testid="db-min-guarantee-input"
                  />
                  <p className="text-xs text-zinc-500">
                    If daily earning falls below this, shortfall is highlighted.
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                  <div>
                    <div className="text-sm font-medium">Active</div>
                    <div className="text-xs text-zinc-500">Inactive boys are hidden from new entries.</div>
                  </div>
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="db-active-switch"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="db-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="db-save-btn"
                >
                  {editingId ? "Save Changes" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Name</Th>
              <Th>Phone</Th>
              <Th className="text-right">Min Guarantee</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && boys.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-zinc-500">
                  No delivery boys yet. Add your first one to start recording collections.
                </td>
              </tr>
            )}
            {boys.map((b) => (
              <tr key={b.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`db-row-${b.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-zinc-950">{b.name}</div>
                </td>
                <td className="px-4 py-3 text-zinc-600">
                  {b.phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 text-zinc-400" /> {b.phone}
                    </span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                    {formatINR(b.min_guarantee)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {b.active !== false ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="border-zinc-200 text-zinc-500">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(b)}
                    data-testid={`db-edit-${b.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteId(b.id)}
                    className="text-rose-600 hover:text-rose-700"
                    data-testid={`db-delete-${b.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="min-guarantees">
          <MinGuaranteesTab />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this delivery boy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the delivery boy. Their past collection entries will remain but may lose the name link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="db-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              data-testid="db-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);
